import { FORMAT_REG_INLINE_FN } from "../../lib/vehicle/regValidation.js";
import { findMakeByDbMake, findMakeBySlug, slugifyMake, slugifyModel } from "../../lib/vehicle/makes.js";

/** Motco-style recent registration memory (localStorage only). SBR + SL. */

export const RECENT_REG_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const RECENT_REG_MAX = 3;

const STORAGE_KEYS = {
  sbr: "sbr_recentRegs_v1",
  sl: "sl_recentRegs_v1",
  servicebyreg: "sbr_recentRegs_v1",
  servicelad: "sl_recentRegs_v1",
};

export function storageKeyForBrand(brandKey) {
  return STORAGE_KEYS[brandKey] || null;
}

export function normaliseRecentReg(value = "") {
  return String(value || "").replace(/\s+/g, "").toUpperCase();
}

/** Display helper: space for 7-char UK plates (AB12 CDE). */
export function formatRecentReg(value = "") {
  const reg = normaliseRecentReg(value);
  return reg.length === 7 ? `${reg.slice(0, 4)} ${reg.slice(4)}` : reg;
}

function getLocalStorage() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readFresh(storageKey) {
  const storage = getLocalStorage();
  if (!storage) return [];
  try {
    const now = Date.now();
    const parsed = JSON.parse(storage.getItem(storageKey) || "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => !!entry?.reg && (!entry.ts || now - entry.ts < RECENT_REG_TTL_MS));
  } catch {
    return [];
  }
}

export function listRecent(brandKey, max = RECENT_REG_MAX) {
  const storageKey = storageKeyForBrand(brandKey);
  if (!storageKey) return [];
  return readFresh(storageKey).slice(0, max);
}

export function rememberReg(brandKey, reg, metadata = {}) {
  const storageKey = storageKeyForBrand(brandKey);
  if (!storageKey) return [];
  const storage = getLocalStorage();
  if (!storage) return [];
  const normalised = normaliseRecentReg(reg);
  if (!normalised) return listRecent(brandKey);
  try {
    const now = Date.now();
    const rest = readFresh(storageKey).filter((entry) => entry.reg !== normalised);
    const item = { reg: normalised, ts: now };
    if (metadata?.make) item.make = String(metadata.make).trim();
    if (metadata?.model) item.model = String(metadata.model).trim();
    if (metadata?.year) item.year = Number(metadata.year) || undefined;
    const next = [item, ...rest].slice(0, RECENT_REG_MAX);
    storage.setItem(storageKey, JSON.stringify(next));
    return next;
  } catch {
    return listRecent(brandKey);
  }
}

/**
 * Inline script for successful free vehicle pages (SBR / SL only).
 * Writes after results are known; never sends plates to analytics.
 */
export function rememberRegInlineScript(brandKey, reg, metadata = {}) {
  const storageKey = storageKeyForBrand(brandKey);
  const normalised = normaliseRecentReg(reg);
  if (!storageKey || !normalised) return "";
  const keyJson = JSON.stringify(storageKey);
  const regJson = JSON.stringify(normalised);
  const makeJson = JSON.stringify(metadata?.make ? String(metadata.make).trim() : "");
  const modelJson = JSON.stringify(metadata?.model ? String(metadata.model).trim() : "");
  const yearJson = JSON.stringify(metadata?.year ? Number(metadata.year) || null : null);
  const ttl = String(RECENT_REG_TTL_MS);
  const max = String(RECENT_REG_MAX);
  return `<script>(function(){try{var k=${keyJson},r=${regJson},mk=${makeJson},md=${modelJson},yr=${yearJson},ttl=${ttl},max=${max},ls=localStorage;if(!ls||!r)return;var now=Date.now();var list=[];try{list=JSON.parse(ls.getItem(k)||"[]");}catch(e){list=[];}if(!Array.isArray(list))list=[];list=list.filter(function(e){return e&&e.reg&&e.reg!==r&&(!e.ts||now-e.ts<ttl);});var item={reg:r,ts:now};if(mk)item.make=mk;if(md)item.model=md;if(yr)item.year=yr;list=[item].concat(list).slice(0,max);ls.setItem(k,JSON.stringify(list));}catch(e){}})();</script>`;
}

/**
 * Find recent vehicle search for a given make (e.g. "volkswagen" or "audi").
 */
export function getRecentVehicleForMake(brandKey, makeSlugOrName = "") {
  if (!makeSlugOrName) return null;
  const storageKey = storageKeyForBrand(brandKey);
  if (!storageKey) return null;
  const recent = readFresh(storageKey);
  const makeDef = findMakeBySlug(makeSlugOrName) || findMakeByDbMake(makeSlugOrName);
  const targetDbMakes = makeDef ? makeDef.dbMakes.map((m) => m.toUpperCase()) : [String(makeSlugOrName).toUpperCase()];
  const targetSlug = makeDef ? makeDef.slug : slugifyMake(makeSlugOrName);

  for (const entry of recent) {
    if (!entry.make) continue;
    const entryMakeUpper = String(entry.make).toUpperCase().trim();
    if (targetDbMakes.includes(entryMakeUpper) || slugifyMake(entry.make) === targetSlug) {
      const modelSlug = entry.model ? slugifyModel(entry.model) : "";
      return {
        reg: entry.reg,
        make: entry.make,
        model: entry.model,
        year: entry.year,
        makeSlug: targetSlug,
        modelSlug,
      };
    }
  }
  return null;
}

/**
 * Recent chips under the free vehicle page "check another" form (SBR).
 * Click fills the field and runs the same client navigation as Motco.
 */
export function recentRegChipsInlineMarkup(brandKey, formId = "sbr-vehicle-search") {
  const storageKey = storageKeyForBrand(brandKey);
  if (!storageKey) return "";
  const keyJson = JSON.stringify(storageKey);
  const formJson = JSON.stringify(formId);
  const ttl = String(RECENT_REG_TTL_MS);
  const max = String(RECENT_REG_MAX);
  return `<div class="recent-regs" id="vehicle-recent-regs" hidden role="group" aria-label="Recent searches"></div><script>(function(){try{var k=${keyJson},formId=${formJson},ttl=${ttl},max=${max},wrap=document.getElementById("vehicle-recent-regs"),form=document.getElementById(formId);if(!wrap||!form)return;var ls=localStorage;if(!ls)return;var now=Date.now(),list=[];try{list=JSON.parse(ls.getItem(k)||"[]");}catch(e){list=[];}if(!Array.isArray(list))list=[];list=list.filter(function(e){return e&&e.reg&&(!e.ts||now-e.ts<ttl);}).slice(0,max);if(!list.length)return;${FORMAT_REG_INLINE_FN}wrap.hidden=false;list.forEach(function(entry){var btn=document.createElement("button");btn.type="button";btn.className="recent-regs-chip";btn.textContent=fmt(entry.reg);btn.setAttribute("aria-label","Check "+fmt(entry.reg));btn.addEventListener("click",function(){var raw=String(entry.reg||"").toUpperCase().replace(/[^A-Z0-9]/g,"");if(!raw)return;if(form.vrm)form.vrm.value=fmt(entry.reg);window.location.href="/vehicle/"+raw.toLowerCase();});wrap.appendChild(btn);});}catch(e){}})();</script>`;
}
